"use client";

import { useMemo, useState } from "react";
import {
  Inbox,
  Send,
  AlertCircle,
  AlertTriangle,
  Info,
  Plus,
  X,
  Loader2,
} from "lucide-react";
import { useStore } from "@/lib/store";
import type {
  Feedback,
  FeedbackStatus,
  FeedbackSeverity,
  Workspace,
} from "@/lib/types";
import { formatRelative } from "@/lib/utils";
import { Avatar } from "@/components/ui/Avatar";

const STATUS_LABEL: Record<FeedbackStatus, { label: string; color: string }> = {
  new: { label: "New", color: "var(--accent)" },
  triaged: { label: "Triaged", color: "var(--blue)" },
  resolved: { label: "Resolved", color: "var(--green)" },
  wontfix: { label: "Won't fix", color: "var(--fg-muted)" },
  duplicate: { label: "Duplicate", color: "var(--fg-muted)" },
};

const SEVERITY_META: Record<FeedbackSeverity, { label: string; icon: typeof AlertCircle; color: string }> = {
  high: { label: "High", icon: AlertCircle, color: "var(--red)" },
  medium: { label: "Med", icon: AlertTriangle, color: "var(--orange)" },
  low: { label: "Low", icon: Info, color: "var(--fg-muted)" },
};

export function FeedbackList({
  mode,
  canWrite,
}: {
  mode: "inbox" | "outbox";
  canWrite: boolean;
}) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const allFeedback = useStore((s) => s.feedback);
  const workspaces = useStore((s) => s.workspaces);
  const issues = useStore((s) => s.issues);
  const users = useStore((s) => s.users);
  const selectedFeedbackId = useStore((s) => s.selectedFeedbackId);
  const selectFeedback = useStore((s) => s.selectFeedback);
  const workspaceLinks = useStore((s) => s.workspaceLinks);

  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | "all">(
    mode === "inbox" ? "new" : "all"
  );
  const [composing, setComposing] = useState(false);

  // Workspaces this one can submit feedback INTO (its outbound links).
  const outboundTargets = useMemo(() => {
    const ids = new Set(
      workspaceLinks
        .filter((l) => l.sourceWorkspaceId === activeWorkspaceId)
        .map((l) => l.targetWorkspaceId)
    );
    return workspaces.filter((w) => ids.has(w.id));
  }, [workspaceLinks, workspaces, activeWorkspaceId]);

  const list = useMemo(() => {
    return allFeedback.filter((f) => {
      const sideMatch =
        mode === "inbox"
          ? f.targetWorkspaceId === activeWorkspaceId
          : f.sourceWorkspaceId === activeWorkspaceId;
      if (!sideMatch) return false;
      if (statusFilter !== "all" && f.status !== statusFilter) return false;
      return true;
    });
  }, [allFeedback, activeWorkspaceId, statusFilter, mode]);

  // Group by other side's workspace (source for inbox, target for outbox)
  const grouped = useMemo(() => {
    const byOther = new Map<string, Feedback[]>();
    for (const f of list) {
      const otherWsId = mode === "inbox" ? f.sourceWorkspaceId : f.targetWorkspaceId;
      const arr = byOther.get(otherWsId) ?? [];
      arr.push(f);
      byOther.set(otherWsId, arr);
    }
    return [...byOther.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [list, mode]);

  const newCount = useMemo(() => list.filter((f) => f.status === "new").length, [list]);

  function startComposing() {
    if (!canWrite) return;
    setComposing(true);
  }

  return (
    <div className="flex h-full">
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-[var(--border)]">
          {mode === "inbox" ? (
            <Inbox size={16} className="text-[var(--accent)]" />
          ) : (
            <Send size={16} className="text-[var(--accent)]" />
          )}
          <h2 className="text-[14px] font-semibold">
            {mode === "inbox" ? "Triage" : "Feedback outbox"}
          </h2>
          <span className="text-[11.5px] text-[var(--fg-tertiary)]">·</span>
          <span className="text-[12px] text-[var(--fg-secondary)]">
            {list.length} item{list.length === 1 ? "" : "s"}
            {mode === "inbox" && newCount > 0 && (
              <>
                {" "}
                · <span className="text-[var(--accent)]">{newCount} new</span>
              </>
            )}
          </span>

          <div className="ml-auto flex items-center gap-1">
            {(["all", "new", "triaged", "resolved", "wontfix", "duplicate"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  statusFilter === s
                    ? "bg-[var(--accent)] text-white"
                    : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
                }`}
              >
                {s === "all" ? "All" : STATUS_LABEL[s].label}
              </button>
            ))}
            {canWrite && (
              <button
                onClick={startComposing}
                disabled={outboundTargets.length === 0}
                title={
                  outboundTargets.length === 0
                    ? "No outbound links yet — add one in Workspace members → Linked workspaces."
                    : "Submit a new feedback item to a linked workspace"
                }
                className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[11.5px] font-medium transition-colors"
              >
                <Plus size={11} />
                New feedback
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {grouped.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8 text-[var(--fg-muted)]">
              {mode === "inbox" ? (
                <>
                  <Inbox size={32} className="mb-3 opacity-40" />
                  <div className="text-[13px] font-medium mb-1">Inbox is empty</div>
                  <div className="text-[11.5px] max-w-sm">
                    Linked consumer workspaces will post feedback here. Add a link in the
                    Workspace members modal &rarr; Linked workspaces.
                  </div>
                </>
              ) : (
                <>
                  <Send size={32} className="mb-3 opacity-40" />
                  <div className="text-[13px] font-medium mb-1">Nothing sent yet</div>
                  <div className="text-[11.5px] max-w-sm">
                    Agents in this workspace can submit feedback to a linked dev workspace via
                    the <code>submit_feedback</code> MCP tool.
                  </div>
                </>
              )}
            </div>
          ) : (
            grouped.map(([otherWsId, items]) => {
              const otherWs = workspaces.find((w) => w.id === otherWsId);
              return (
                <div key={otherWsId}>
                  <div className="px-6 py-2 sticky top-0 bg-[var(--bg)] border-b border-[var(--border)] flex items-center gap-2">
                    <span
                      className="w-4 h-4 rounded flex items-center justify-center text-[8px] font-bold"
                      style={{ background: otherWs?.color ?? "var(--fg-muted)", color: "white" }}
                    >
                      {otherWs?.icon ?? "?"}
                    </span>
                    <span className="text-[11.5px] font-semibold uppercase tracking-wider text-[var(--fg-secondary)]">
                      {mode === "inbox" ? "from " : "to "}
                      {otherWs?.name ?? "Unknown workspace"}
                    </span>
                    <span className="text-[10.5px] text-[var(--fg-muted)]">{items.length}</span>
                  </div>
                  <div className="divide-y divide-[var(--border)]/40">
                    {items.map((f) => {
                      const Sev = SEVERITY_META[f.severity];
                      const StatusMeta = STATUS_LABEL[f.status];
                      const linkedIssue = f.linkedIssueId
                        ? issues.find((i) => i.id === f.linkedIssueId)
                        : null;
                      const submitter = users.find((u) => u.id === f.submitterId);
                      return (
                        <button
                          key={f.id}
                          onClick={() => selectFeedback(f.id)}
                          className={`w-full text-left px-6 py-2.5 flex items-center gap-3 hover:bg-[var(--bg-hover)] transition-colors ${
                            selectedFeedbackId === f.id ? "bg-[var(--bg-hover)]" : ""
                          }`}
                        >
                          <Sev.icon size={13} style={{ color: Sev.color }} className="shrink-0" />
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0"
                            style={{
                              background: `color-mix(in srgb, ${StatusMeta.color} 18%, transparent)`,
                              color: StatusMeta.color,
                            }}
                          >
                            {StatusMeta.label}
                          </span>
                          <span className="text-[12.5px] text-[var(--fg)] flex-1 min-w-0 truncate">
                            {f.title}
                          </span>
                          {linkedIssue && (
                            <span className="px-1.5 py-0.5 rounded bg-[var(--accent-bg)] text-[var(--accent)] text-[10px] font-mono shrink-0">
                              {linkedIssue.identifier}
                            </span>
                          )}
                          {f.tags.slice(0, 2).map((t) => (
                            <span
                              key={t}
                              className="text-[10px] text-[var(--fg-muted)] bg-[var(--bg-tertiary)] px-1.5 py-0.5 rounded-full shrink-0"
                            >
                              #{t}
                            </span>
                          ))}
                          {submitter && (
                            <Avatar user={submitter} size={18} className="shrink-0" />
                          )}
                          <span className="text-[10.5px] text-[var(--fg-muted)] tabular-nums shrink-0 w-14 text-right">
                            {formatRelative(f.createdAt)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {canWrite && composing && (
        <NewFeedbackModal
          targets={outboundTargets}
          canWrite={canWrite}
          onClose={() => setComposing(false)}
        />
      )}
    </div>
  );
}

function NewFeedbackModal({
  targets,
  canWrite,
  onClose,
}: {
  targets: Workspace[];
  canWrite: boolean;
  onClose: () => void;
}) {
  const submitFeedback = useStore((s) => s.submitFeedback);
  const [targetId, setTargetId] = useState(targets[0]?.id ?? "");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState<FeedbackSeverity>("medium");
  const [tagsRaw, setTagsRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit() {
    if (!canWrite || !title.trim() || !targetId) return;
    setBusy(true);
    setErr(null);
    const tags = tagsRaw
      .split(/[,\s]+/)
      .map((t) => t.replace(/^#/, "").trim())
      .filter(Boolean);
    const created = await submitFeedback({
      targetWorkspaceId: targetId,
      title: title.trim(),
      body,
      severity,
      tags,
    });
    setBusy(false);
    if (created) {
      onClose();
    } else {
      setErr("Submission failed. Check that the workspace link still exists.");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-[12vh]"
      onClick={onClose}
    >
      <div
        className="w-[560px] max-w-[90vw] rounded-lg border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--border)]">
          <Send size={13} className="text-[var(--accent)]" />
          <h3 className="text-[13.5px] font-semibold flex-1">New feedback</h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)]"
          >
            <X size={12} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--fg-muted)] block mb-1">
              Send to
            </label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
            >
              {targets.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.icon} {w.name} ({w.category})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--fg-muted)] block mb-1">
              Title
            </label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="One scannable sentence — what does the dev side need to know?"
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
            />
          </div>

          <div>
            <label className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--fg-muted)] block mb-1">
              Body (markdown)
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="3–8 line TL;DR. User quotes, repro steps, your interpretation. Wikilinks [[Doc title]] and @ABC-123 supported."
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-2 text-[13px] outline-none focus:border-[var(--accent)] resize-y min-h-[140px] font-mono leading-relaxed"
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--fg-muted)] block mb-1">
                Severity
              </label>
              <div className="flex items-center rounded-md border border-[var(--border)] overflow-hidden">
                {(["low", "medium", "high"] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSeverity(s)}
                    className={`flex-1 px-2 py-1 text-[11.5px] font-medium transition-colors ${
                      severity === s
                        ? "bg-[var(--accent)] text-white"
                        : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <label className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--fg-muted)] block mb-1">
                Tags (comma or space separated)
              </label>
              <input
                value={tagsRaw}
                onChange={(e) => setTagsRaw(e.target.value)}
                placeholder="bug, ux, perf"
                className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
              />
            </div>
          </div>

          {err && (
            <div className="rounded-md bg-[var(--red)]/12 border border-[var(--red)]/30 px-3 py-2 text-[12px] text-[var(--red)]">
              {err}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--border)] bg-[var(--bg-secondary)]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-[12px] text-[var(--fg-tertiary)] hover:text-[var(--fg-secondary)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || !targetId || busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-50 text-white text-[12px] font-medium transition-colors"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            {busy ? "Sending…" : "Send feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}
